import { useEffect, useMemo } from 'react';
import { TextKey } from '@mews-commander/enums';
import {
    AssistantSuggestion,
    AssistantSuggestionEntityType,
    AssistantSuggestionType,
} from '@mews-commander/data-access';
import { useAssistantAutocompleteQuery } from '../../query/useAssistantAutocompleteQuery';
import { AutoCompleteContainer } from '../styles';
import { startsWithMqlTriggerCharacter } from '../MqlHelper';
import { MqlTriggerCharacter } from '../Mql';
import { Suggestions } from './Suggestions';

interface AutocompleteProps {
    searchTerm: string;
    isMql: boolean;
    focused: boolean;
    selectedIndex: number;
    setSelectedSuggestion: (suggestion: AssistantSuggestion) => void;
    handleSuggestionSelection: (suggestion: AssistantSuggestion) => void;
    enterpriseIds?: string[];
}

export const Autocomplete = ({
    searchTerm,
    isMql,
    focused,
    selectedIndex,
    setSelectedSuggestion,
    handleSuggestionSelection,
    enterpriseIds,
}: AutocompleteProps) => {
    const { data: autocomplete } = useAssistantAutocompleteQuery(searchTerm, enterpriseIds ?? [], true);

    const searchTermHasFrontSlash = startsWithMqlTriggerCharacter(searchTerm);
    const sanitizedSearchTerm = searchTermHasFrontSlash ? searchTerm.split(MqlTriggerCharacter)[1] : searchTerm;

    const suggestionsHistory = useMemo(() => autocomplete?.suggestionsHistory ?? [], [autocomplete]);
    const commands = useMemo(() => autocomplete?.commands ?? [], [autocomplete]);
    const commandOperators = useMemo(() => autocomplete?.commandOperators ?? [], [autocomplete]);
    const entities = useMemo(() => autocomplete?.entities ?? [], [autocomplete]);
    const parameters = useMemo(() => autocomplete?.parameters ?? [], [autocomplete]);
    const operators = useMemo(() => autocomplete?.operators ?? [], [autocomplete]);
    const values = useMemo(() => autocomplete?.values ?? [], [autocomplete]);
    const texts = useMemo(() => autocomplete?.texts ?? [], [autocomplete]);

    const show = focused && isMql; // && (isFetching || !!autocomplete?.hasResults);

    const allSuggestions = useMemo(
        () => [...commands, ...commandOperators, ...entities, ...parameters, ...operators, ...values, ...texts],
        [commands, commandOperators, entities, parameters, operators, values, texts]
    );

    const searchTermWithoutPreviousKeyword = getSearchTermWithoutPreviousKeyword(
        sanitizedSearchTerm,
        suggestionsHistory
    );

    let updatedSelectedIndex = selectedIndex > allSuggestions.length - 1 ? allSuggestions.length - 1 : selectedIndex;
    updatedSelectedIndex = updatedSelectedIndex < -1 ? -1 : updatedSelectedIndex;

    useEffect(() => {
        const selectedSuggestion = allSuggestions[updatedSelectedIndex];
        if (selectedSuggestion) {
            setSelectedSuggestion(allSuggestions[updatedSelectedIndex]);
        }
    }, [allSuggestions, updatedSelectedIndex, setSelectedSuggestion]);

    const handleSuggestionClick = (suggestion: AssistantSuggestion) => handleSuggestionSelection(suggestion);

    const suggestionsLists = getSuggestionLists(
        commands,
        commandOperators,
        entities,
        parameters,
        operators,
        values,
        texts
    );

    const entityTextKey: Record<AssistantSuggestionEntityType, Mews.TextKey> = {
        [AssistantSuggestionEntityType.None]: TextKey.None,
        [AssistantSuggestionEntityType.Guest]: TextKey.Guest,
        [AssistantSuggestionEntityType.Note]: TextKey.Note,
        [AssistantSuggestionEntityType.Reservation]: TextKey.Reservation,
    };
    return show ? (
        <AutoCompleteContainer show={show}>
            {suggestionsLists.map(({ type, textKey, suggestions }) => {
                if (type === AssistantSuggestionType.Value) {
                    const valueByEntityType = getValueByEntityType(suggestions);

                    const entityTypes: AssistantSuggestionEntityType[] = [
                        AssistantSuggestionEntityType.None,
                        AssistantSuggestionEntityType.Guest,
                        AssistantSuggestionEntityType.Reservation,
                        AssistantSuggestionEntityType.Note,
                    ];

                    return entityTypes.map((entity) => {
                        const entitySuggestions = valueByEntityType[entity] || [];
                        return (
                            <Suggestions
                                key={entity}
                                titleTextKey={entityTextKey[entity]}
                                suggestions={entitySuggestions}
                                allSuggestions={allSuggestions}
                                searchTerm={searchTermWithoutPreviousKeyword}
                                selectedIndex={updatedSelectedIndex}
                                handleClick={handleSuggestionClick}
                            />
                        );
                    });
                }
                return (
                    <Suggestions
                        key={type}
                        titleTextKey={textKey}
                        suggestions={suggestions}
                        allSuggestions={allSuggestions}
                        searchTerm={searchTermWithoutPreviousKeyword}
                        selectedIndex={updatedSelectedIndex}
                        handleClick={handleSuggestionClick}
                    />
                );
            })}
        </AutoCompleteContainer>
    ) : null;
};

const getValueByEntityType: (
    suggestions: AssistantSuggestion[]
) => Record<AssistantSuggestionEntityType, AssistantSuggestion[]> = (suggestions: AssistantSuggestion[]) =>
    suggestions.reduce(
        (acc, suggestion) => {
            if (suggestion.entityType) {
                if (acc[suggestion.entityType]) {
                    acc[suggestion.entityType].push(suggestion);
                } else {
                    acc[suggestion.entityType] = [suggestion];
                }
            }
            return acc;
        },
        {} as Record<AssistantSuggestionEntityType, AssistantSuggestion[]>
    );

const getSuggestionLists: (
    commands: AssistantSuggestion[],
    commandOperators: AssistantSuggestion[],
    entities: AssistantSuggestion[],
    parameters: AssistantSuggestion[],
    operators: AssistantSuggestion[],
    values: AssistantSuggestion[],
    texts: AssistantSuggestion[]
) => {
    type: AssistantSuggestionType;
    textKey: Mews.TextKey;
    suggestions: AssistantSuggestion[];
}[] = (
    commands: AssistantSuggestion[],
    commandOperators: AssistantSuggestion[],
    entities: AssistantSuggestion[],
    parameters: AssistantSuggestion[],
    operators: AssistantSuggestion[],
    values: AssistantSuggestion[],
    texts: AssistantSuggestion[]
) => [
    { type: AssistantSuggestionType.Command, textKey: TextKey.AssistantKeywordCommands, suggestions: commands },
    {
        type: AssistantSuggestionType.CommandOperator,
        textKey: TextKey.AssistantKeywordOperators,
        suggestions: commandOperators,
    },
    { type: AssistantSuggestionType.Entity, textKey: TextKey.AssistantKeywordEntities, suggestions: entities },
    {
        type: AssistantSuggestionType.Parameter,
        textKey: TextKey.AssistantKeywordParameters,
        suggestions: parameters,
    },
    { type: AssistantSuggestionType.Operator, textKey: TextKey.AssistantKeywordOperators, suggestions: operators },
    { type: AssistantSuggestionType.Value, textKey: TextKey.AssistantKeywordOperators, suggestions: values },
    { type: AssistantSuggestionType.Text, textKey: TextKey.Content, suggestions: texts },
];

const getSearchTermWithoutPreviousKeyword = (searchTerm: string, allSuggestions: AssistantSuggestion[]): string => {
    let returnValue = searchTerm;
    allSuggestions.forEach((suggestion) => {
        if (searchTerm.includes(suggestion.text)) {
            returnValue = searchTerm.replace(suggestion.text, '').trim();
        }
    });
    return returnValue;
};
